import React, { useState } from 'react';
import {
  IAuditoriaEstoqueSemMovimentoResultado,
  IAuditoriaEstoqueSemMovimentoItem,
  IAuditoriaEstoqueSemMovimentoConta,
} from '../interfaces/IAuditoriaEstoqueSemMovimento';
import { formatDate as formatDateTime } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaEstoqueSemMovimentoResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'          ? 'badge-light-danger' :
    status === 'ALERTA'        ? 'badge-light-warning' :
    status === 'AVISO'         ? 'badge-light-warning' :
    status === 'NAO_ANALISADO' ? 'badge-light-secondary' :
    status === 'SEM_SALDO'     ? 'badge-light-secondary' :
                                 'badge-light-success';
  const label = status === 'NAO_ANALISADO' ? 'NÃO ANALISADO' : status === 'SEM_SALDO' ? 'SEM SALDO' : status;
  return <span className={`badge ${cls}`}>{label}</span>;
};

// COR-205: tabela de TODAS as contas de estoque localizadas — memória de cálculo completa
// (saldo inicial, entradas, saídas, saldo final, última saída, dias sem saída), mesmo para
// contas que não geraram ocorrência (sem saldo, ou dentro do prazo).
const TabelaContasAnalisadas: React.FC<{ contas: IAuditoriaEstoqueSemMovimentoConta[] }> = ({ contas }) => (
  <div className='table-responsive mb-6'>
    <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-2 fs-8'>
      <thead>
        <tr className='fw-bolder text-muted'>
          <th>Empresa</th>
          <th>Conta</th>
          <th className='text-end'>Saldo Inicial</th>
          <th className='text-end'>Entradas</th>
          <th className='text-end'>Saídas</th>
          <th className='text-end'>Saldo Final</th>
          <th>Última Saída</th>
          <th className='text-center'>Dias s/ Saída</th>
          <th className='text-center'>Status</th>
        </tr>
      </thead>
      <tbody>
        {contas.map((c, i) => (
          <tr key={i}>
            <td>{c.empresaNome}</td>
            <td>
              <span className='fw-bold'>{c.contaReferencial}</span>
              <br /><span className='text-muted fs-9'>{c.descricaoConta}</span>
            </td>
            <td className='text-end'>{formatCurrency(c.saldoInicial)}</td>
            <td className='text-end'>{formatCurrency(c.totalEntradas)}</td>
            <td className='text-end'>{formatCurrency(c.totalSaidas)}</td>
            <td className='text-end fw-bold'>{formatCurrency(c.saldoFinal)}</td>
            <td>{formatDate(c.ultimaSaidaData)}</td>
            <td className='text-center'>{c.diasSemSaida ?? '—'}</td>
            <td className='text-center'><StatusBadge status={c.status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// COR-289: drill-down a nível de lançamento — todo AVISO/ALERTA/ERRO precisa mostrar não só o
// saldo agregado, mas o histórico de lançamentos que compõem o estoque parado.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IAuditoriaEstoqueSemMovimentoItem }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mb-3'>
      <strong className='fs-8'>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDateTime(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const ItemExpandivel: React.FC<{ item: IAuditoriaEstoqueSemMovimentoItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandido(!expandido)}>
        <td>
          <span className='fw-bold'>{item.empresaNome}</span>
          <br />
          <span className='text-muted fs-8'>{item.cnpj}</span>
        </td>
        <td>
          <span className='fw-bold'>{item.contaReferencial}</span>
          <br />
          <span className='text-muted fs-8'>{item.descricaoConta}</span>
        </td>
        <td className='fw-bold'>{formatCurrency(item.saldoEstoque)}</td>
        <td>
          {item.houveSaida
            ? formatDate(item.ultimaMovimentacaoData)
            : <span className='text-danger fw-bold'>Nunca houve saída</span>}
        </td>
        <td className='fw-bold'>
          <span className={item.diasParados >= 365 ? 'text-danger' : item.diasParados >= 270 ? 'text-warning' : 'text-warning'}>
            {item.diasParados} dias
          </span>
        </td>
        <td><StatusBadge status={item.status} /></td>
        <td className='text-end'>{expandido ? '▲' : '▼'}</td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={7} className='bg-light-secondary p-4'>
            <div className='mb-3'>
              <strong>Diagnóstico:</strong>
              <p className='text-muted mt-1 mb-2'>{item.mensagemDiagnostico}</p>
            </div>

            <BlocoLancamentosRelacionados item={item} />

            {item.status === 'ERRO' && (
              <>
                <div className='row mb-4'>
                  <div className='col-3 text-center border-end'>
                    <div className='fs-5 fw-bolder text-muted'>{formatCurrency(item.saldoEstoque)}</div>
                    <div className='text-muted fs-8'>Base de Cálculo (Saldo)</div>
                  </div>
                  <div className='col-2 text-center border-end'>
                    <div className='fs-5 fw-bolder text-danger'>{formatCurrency(item.tributosIrpjCsll)}</div>
                    <div className='text-muted fs-8'>IRPJ/CSLL (34%)</div>
                  </div>
                  <div className='col-2 text-center border-end'>
                    <div className='fs-5 fw-bolder text-danger'>{formatCurrency(item.multaArt44)}</div>
                    <div className='text-muted fs-8'>Multa Art. 44 (75%)</div>
                  </div>
                  <div className='col-2 text-center border-end'>
                    <div className='fs-5 fw-bolder text-warning'>{formatCurrency(item.jurosSelic)}</div>
                    <div className='text-muted fs-8'>Juros SELIC</div>
                  </div>
                  <div className='col-3 text-center'>
                    <div className='fs-4 fw-bolder text-danger'>{formatCurrency(item.passivoTributario)}</div>
                    <div className='text-muted fs-8'>Passivo Total</div>
                  </div>
                </div>

                <div className='alert alert-danger mt-3 py-2'>
                  <strong>Regularização obrigatória:</strong> Estoque sem saída há ≥ 365 dias. Ativo
                  superavaliado com impacto no Patrimônio Líquido. Reconheça a perda por redução ao valor
                  realizável líquido (baixa ou provisão) e documente a obsolescência conforme NBC TG 16 (CPC 16)
                  Art. 28. Lei 6.404/76 Art. 183 II, Lei 9.430/96 Art. 44 I.
                </div>
              </>
            )}

            {item.status === 'ALERTA' && (
              <div className='alert alert-warning mt-3 py-2'>
                <strong>Ação recomendada:</strong> Estoque parado entre 270 e 364 dias. Avalie a necessidade
                de provisão para redução ao valor realizável líquido. NBC TG 16 (CPC 16) Art. 28,
                Lei 6.404/76 Art. 183 II.
              </div>
            )}

            {item.status === 'AVISO' && (
              <div className='alert alert-warning mt-3 py-2'>
                <strong>Revisão sugerida:</strong> Estoque parado entre 180 e 269 dias. Revise o giro e
                classifique como de difícil realização se não houver perspectiva de venda/consumo próxima.
                NBC TG 16 (CPC 16).
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaEstoqueSemMovimentoDetalhe: React.FC<Props> = ({ resultado }) => {
  const [mostrarContas, setMostrarContas] = useState(false);
  const bannerCor =
    resultado.status === 'ERRO'          ? 'danger' :
    resultado.status === 'ALERTA'        ? 'warning' :
    resultado.status === 'AVISO'         ? 'warning' :
    resultado.status === 'NAO_ANALISADO' ? 'secondary' :
                                           'success';

  return (
    <div className='card card-custom card-stretch'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>
            Estoques Sem Movimentação — Detalhamento (≥ 180 dias)
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            NBC TG 16 (CPC 16) · Lei 6.404/76 Art. 183 II · Lei 9.430/96 Art. 44
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {resultado.status !== 'CONCILIADO' && (
          <div className={`alert alert-${bannerCor} py-3 mb-5`}>
            <strong>{resultado.status === 'NAO_ANALISADO' ? 'NÃO ANALISADO' : resultado.status}:</strong>{' '}
            {resultado.mensagemDirecionamento}
          </div>
        )}

        {/* COR-205: transparência do escopo — quantas contas de estoque foram localizadas. */}
        {resultado.status !== 'NAO_ANALISADO' && (
          <div className='mb-5'>
            <div className='text-muted fs-8 mb-2'>
              {resultado.quantidadeContasLocalizadas} conta(s) de estoque localizada(s) e analisada(s).
            </div>
            {resultado.contasAnalisadas.length > 0 && (
              <button
                className='btn btn-sm btn-light-primary'
                onClick={() => setMostrarContas(!mostrarContas)}
              >
                <i className={`bi bi-chevron-${mostrarContas ? 'up' : 'down'} me-1`} />
                {mostrarContas ? 'Ocultar' : 'Exibir'} todas as contas analisadas ({resultado.contasAnalisadas.length})
              </button>
            )}
            {mostrarContas && <div className='mt-3'><TabelaContasAnalisadas contas={resultado.contasAnalisadas} /></div>}
          </div>
        )}

        <div className='row mb-6'>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-danger'>{resultado.totalErros}</div>
            <div className='text-muted'>Erros (≥ 365 dias)</div>
            <div className='text-muted fs-8'>Estoque obsoleto — baixa/provisão obrigatória</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAlertas}</div>
            <div className='text-muted'>Alertas (270–364 dias)</div>
            <div className='text-muted fs-8'>Avalie provisão para valor realizável</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAvisos}</div>
            <div className='text-muted'>Avisos (180–269 dias)</div>
            <div className='text-muted fs-8'>Baixo giro — revise giro de estoques</div>
          </div>
          <div className='col-3 text-center'>
            <div className='fs-1 fw-bolder text-danger'>{formatCurrency(resultado.passivoTributarioTotal)}</div>
            <div className='text-muted'>Passivo Tributário Total</div>
            <div className='text-muted fs-8'>IRPJ/CSLL 34% + Multa 75% + SELIC</div>
          </div>
        </div>

        {resultado.itens.length === 0 ? (
          resultado.status === 'NAO_ANALISADO' ? (
            <div className='text-muted fw-bold fs-5 text-center py-8'>
              ⚠ Nenhuma conta de estoque foi localizada nesta ECD — ver mensagem acima. Esta
              auditoria não foi analisada, não é uma confirmação de giro adequado de estoque.
            </div>
          ) : (
            <div className='text-success fw-bold fs-5 text-center py-8'>
              ✓ Nenhuma conta de estoque com saída ausente há {'≥'} 180 dias — de{' '}
              {resultado.quantidadeContasLocalizadas} conta(s) analisada(s).
            </div>
          )
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Conta Referencial</th>
                  <th>Saldo Estoque</th>
                  <th>Última Saída</th>
                  <th>Dias Parados</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemExpandivel key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <details className='mt-6'>
          <summary className='text-muted fw-bold cursor-pointer'>Base Legal</summary>
          <p className='text-muted mt-2 fs-7'>{resultado.fundamentacaoLegal}</p>
        </details>
      </div>
    </div>
  );
};

export default AuditoriaEstoqueSemMovimentoDetalhe;
